import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";
import type { CreateEnquiryRequest, WalkerDetailDto } from "../types/walkers";

type LoadState = "idle" | "loading" | "loaded" | "error";
type SubmitState = "idle" | "submitting" | "submitted" | "error";

type EnquiryFormState = {
  ownerEmail: string;
  ownerName: string;
  ownerPostcode: string;
  dogName: string;
  dogBreed: string;
  dogAgeText: string;
  walkFrequency: string;
  message: string;
};

const OWNER_POSTCODE_STORAGE_KEY = "wnm.ownerPostcode";
const OWNER_EMAIL_STORAGE_KEY = "wnm.ownerEmail";

function formatLastActive(value?: string | null): string {
  if (!value) return "Recently active";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Recently active";

  const diffMs = Date.now() - d.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute) return "Active just now";
  if (diffMs < hour) {
    const mins = Math.max(1, Math.floor(diffMs / minute));
    return `Active ${mins} minute${mins === 1 ? "" : "s"} ago`;
  }
  if (diffMs < day) {
    const hours = Math.max(1, Math.floor(diffMs / hour));
    return `Active ${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.max(1, Math.floor(diffMs / day));
  return `Active ${days} day${days === 1 ? "" : "s"} ago`;
}

function buildDisplayName(walker: WalkerDetailDto | null): string {
  if (!walker) return "";
  const name = [walker.firstName, walker.lastName].filter(Boolean).join(" ").trim();
  return name || walker.displayTitle;
}

function getPrimaryPhoto(walker: WalkerDetailDto | null) {
  if (!walker?.photos?.length) return null;

  const explicitPrimary = walker.photos.find((p) => p.isPrimary);
  if (explicitPrimary) return explicitPrimary;

  const sorted = [...walker.photos].sort((a, b) => a.sortOrder - b.sortOrder);
  return sorted[0] ?? null;
}

function normalizePostcodeInput(value: string): string {
  return value.replace(/\s+/g, " ").trim().toUpperCase();
}

function readStoredOwnerPostcode(): string {
  try {
    return localStorage.getItem(OWNER_POSTCODE_STORAGE_KEY)?.trim() || "";
  } catch {
    return "";
  }
}

function readStoredOwnerEmail(): string {
  try {
    return localStorage.getItem(OWNER_EMAIL_STORAGE_KEY)?.trim() || "";
  } catch {
    return "";
  }
}

const WalkerDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [walker, setWalker] = useState<WalkerDetailDto | null>(null);
  const [state, setState] = useState<LoadState>("idle");
  const [error, setError] = useState("");

  const searchParams = new URLSearchParams(location.search);
  const queryPostcode = searchParams.get("postcode") ?? "";
  const rebookMode = (searchParams.get("from") || "").trim().toLowerCase() === "rebook";
  const previousWalker = (searchParams.get("previousWalker") || "").trim();

  const [form, setForm] = useState<EnquiryFormState>({
    ownerEmail: readStoredOwnerEmail(),
    ownerName: "",
    ownerPostcode: queryPostcode || readStoredOwnerPostcode(),
    dogName: "",
    dogBreed: "",
    dogAgeText: "",
    walkFrequency: "",
    message: "",
  });

  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [ownerReplyPath, setOwnerReplyPath] = useState<string>("");
  const [activePhotoUrl, setActivePhotoUrl] = useState<string>("");

  useEffect(() => {
    async function load() {
      if (!slug?.trim()) {
        setError("Walker profile not found.");
        setState("error");
        return;
      }

      try {
        setState("loading");
        setError("");

        const data = await walkiesClient.getWalkerBySlug(slug);
        setWalker(data);

        const primaryPhoto = getPrimaryPhoto(data);
        setActivePhotoUrl(primaryPhoto?.url ?? "");

        setForm((prev) => ({
          ...prev,
          ownerPostcode:
            prev.ownerPostcode || queryPostcode || readStoredOwnerPostcode(),
          ownerEmail: prev.ownerEmail || readStoredOwnerEmail(),
          message:
            prev.message.trim().length > 0
              ? prev.message
              : rebookMode
              ? `Hi, I'm looking for help with a new booking and would like to check your availability.`
              : "Hi, I'm interested in dog walking help for my dog and would like to know your availability.",
        }));

        setState("loaded");
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load walker profile.";
        setError(message);
        setWalker(null);
        setState("error");
      }
    }

    void load();
  }, [slug, queryPostcode, rebookMode]);

  const locationLine = useMemo(() => {
    if (!walker) return "";
    return [walker.town, walker.county].filter(Boolean).join(", ");
  }, [walker]);

  const displayName = useMemo(() => buildDisplayName(walker), [walker]);

  const primaryPhoto = useMemo(() => getPrimaryPhoto(walker), [walker]);

  const sortedPhotos = useMemo(() => {
    if (!walker?.photos?.length) return [];
    return [...walker.photos].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [walker]);

  const serviceTags = useMemo(() => {
    if (!walker) return [];

    const tags: string[] = [];

    if (walker.soloWalks) tags.push("Solo walks");
    if (walker.groupWalks) tags.push("Group walks");
    if (walker.puppyVisits) tags.push("Puppy visits");
    if (walker.elderlyDogCare) tags.push("Elderly dog care");

    return tags;
  }, [walker]);

  const trustTags = useMemo(() => {
    if (!walker) return [];

    const tags: string[] = [];

    if (walker.dbsChecked) tags.push("DBS checked");
    if (walker.insured) tags.push("Insured");
    if (walker.experienceSummary?.trim()) tags.push("Experienced walker");

    return tags;
  }, [walker]);

  function updateForm<K extends keyof EnquiryFormState>(
    key: K,
    value: EnquiryFormState[K]
  ) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (submitState !== "idle") {
      setSubmitState("idle");
    }

    if (submitError) {
      setSubmitError("");
    }

    if (submitSuccess) {
      setSubmitSuccess("");
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!walker) {
      setSubmitState("error");
      setSubmitError("Walker profile is not loaded.");
      return;
    }

    const ownerEmail = form.ownerEmail.trim();
    const ownerPostcode = normalizePostcodeInput(form.ownerPostcode);
    const message = form.message.trim();

    if (!ownerEmail) {
      setSubmitState("error");
      setSubmitError("Please enter your email address.");
      return;
    }

    if (!message) {
      setSubmitState("error");
      setSubmitError("Please enter a message.");
      return;
    }

    try {
      setSubmitState("submitting");
      setSubmitError("");
      setSubmitSuccess("");
      setOwnerReplyPath("");

      const request: CreateEnquiryRequest = {
        walkerProfileId: walker.walkerProfileId,
        ownerEmail,
        ownerName: form.ownerName.trim() || null,
        ownerPostcode: ownerPostcode || null,
        dogName: form.dogName.trim() || null,
        dogBreed: form.dogBreed.trim() || null,
        dogAgeText: form.dogAgeText.trim() || null,
        walkFrequency: form.walkFrequency.trim() || null,
        message,
      };

      const result = await walkiesClient.createEnquiry(request);

      try {
        localStorage.setItem(OWNER_EMAIL_STORAGE_KEY, ownerEmail);
        if (ownerPostcode) {
          localStorage.setItem(OWNER_POSTCODE_STORAGE_KEY, ownerPostcode);
        }
      } catch {
        // ignore localStorage failures
      }

      const replyParams = new URLSearchParams();

      if (ownerPostcode) {
        replyParams.set("postcode", ownerPostcode);
      }

      if (walker.slug) {
        replyParams.set("walkerSlug", walker.slug);
      }

      if (walker.displayTitle?.trim()) {
        replyParams.set("walkerName", walker.displayTitle.trim());
      }

      const instantReplyPath = result.ownerReplyToken?.trim()
        ? `/reply/${result.ownerReplyToken.trim()}${
            replyParams.toString() ? `?${replyParams.toString()}` : ""
          }`
        : "";

      setOwnerReplyPath(instantReplyPath);

      setSubmitState("submitted");
      setSubmitSuccess(
        instantReplyPath
          ? "Your enquiry has been sent successfully. You can open your secure conversation immediately using the link below. If the walker replies later, you will also receive a fresh secure reply link by email."
          : "Your enquiry has been sent successfully. If the walker replies, you will receive a secure conversation link by email."
      );
      setSubmitError("");

      setForm((prev) => ({
        ...prev,
        dogName: "",
        dogBreed: "",
        dogAgeText: "",
        walkFrequency: "",
        message: rebookMode
          ? "Hi, I'm looking for help with a new booking and would like to check your availability."
          : "",
      }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to send enquiry.";
      setSubmitState("error");
      setSubmitError(message);
    }
  }

  if (state === "loading" || state === "idle") {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.loadingCard}>Loading walker profile...</div>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.errorCard}>
            <h1 style={styles.errorTitle}>We couldn’t load that walker profile</h1>
            <p style={styles.errorText}>
              {error || "Something went wrong while loading the profile."}
            </p>

            <div style={styles.errorActions}>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={() => navigate(-1)}
              >
                Go back
              </button>

              <Link to="/walkers" style={styles.secondaryLinkButton}>
                Back to search
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!walker) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.errorCard}>
            <h1 style={styles.errorTitle}>Walker not found</h1>
            <p style={styles.errorText}>
              This profile may not exist or may no longer be available.
            </p>
            <Link to="/walkers" style={styles.primaryLinkButton}>
              Back to search
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const mainPhotoUrl = activePhotoUrl || primaryPhoto?.url || "";

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topRow}>
          <Link to={`/walkers${location.search || ""}`} style={styles.backLink}>
            ← Back to walkers
          </Link>
        </div>

        <section style={styles.heroCard}>
          <div style={styles.heroGrid}>
            <div>
              <div style={styles.badgeRow}>
                <span style={styles.badge}>Walker profile</span>
                <span style={styles.badgeSoft}>
                  {formatLastActive(walker.lastActivityUtc)}
                </span>
              </div>

              <h1 style={styles.title}>{walker.displayTitle}</h1>

              {displayName && displayName !== walker.displayTitle && (
                <p style={styles.nameLine}>{displayName}</p>
              )}

              {locationLine && <p style={styles.location}>{locationLine}</p>}

              {queryPostcode && (
                <p style={styles.distanceLine}>
                  Searched from <strong>{queryPostcode}</strong>
                </p>
              )}

              {walker.bio && <p style={styles.intro}>{walker.bio}</p>}

              {rebookMode && (
                <div style={styles.rebookBanner}>
                  <div style={styles.rebookBannerTitle}>
                    Looking for a new walker?
                  </div>
                  <div style={styles.rebookBannerText}>
                    {previousWalker
                      ? `You’re viewing an alternative to ${previousWalker}. Send a fresh enquiry below if you’d like to start a new conversation.`
                      : "You’re viewing an alternative walker. Send a fresh enquiry below if you’d like to start a new conversation."}
                  </div>
                </div>
              )}

              <div style={styles.summaryGrid}>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Coverage</div>
                  <div style={styles.summaryValue}>
                    Up to {walker.serviceRadiusMiles} miles
                  </div>
                </div>

                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Experience</div>
                  <div style={styles.summaryValue}>
                    {walker.experienceSummary?.trim() || "Profile available"}
                  </div>
                </div>

                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Location</div>
                  <div style={styles.summaryValue}>{locationLine || "Local area"}</div>
                </div>
              </div>
            </div>

            <div style={styles.sidePanel}>
              <div style={styles.enquiryCard}>
                {mainPhotoUrl ? (
                  <img
                    src={mainPhotoUrl}
                    alt={walker.displayTitle}
                    style={styles.photo}
                  />
                ) : (
                  <div style={styles.photoPlaceholder}>🐾</div>
                )}

                {sortedPhotos.length > 1 && (
                  <div style={styles.photoThumbRow}>
                    {sortedPhotos.map((photo) => {
                      const isActive = photo.url === mainPhotoUrl;

                      return (
                        <button
                          key={photo.photoId}
                          type="button"
                          onClick={() => setActivePhotoUrl(photo.url)}
                          style={{
                            ...styles.thumbButton,
                            ...(isActive ? styles.thumbButtonActive : {}),
                          }}
                        >
                          <img
                            src={photo.url}
                            alt={walker.displayTitle}
                            style={styles.thumbButtonImage}
                          />
                        </button>
                      );
                    })}
                  </div>
                )}

                <h2 style={styles.panelTitle}>Interested in this walker?</h2>
                <p style={styles.panelText}>
                  Send a direct enquiry with a few details about your dog and what
                  kind of help you need.
                </p>

                <div style={styles.trustListCompact}>
                  {trustTags.map((tag) => (
                    <span key={tag} style={styles.trustPill}>
                      ✓ {tag}
                    </span>
                  ))}
                </div>

                <a href="#contact-section" style={styles.primaryLinkButton}>
                  Enquire about this walker
                </a>
              </div>
            </div>
          </div>
        </section>

        <div style={styles.contentGrid}>
          <section style={styles.mainCard}>
            <h2 style={styles.sectionTitle}>About this walker</h2>

            <p style={styles.bodyText}>
              {walker.bio?.trim() ||
                "This walker profile is live. More descriptive profile content can be added as the onboarding flow grows."}
            </p>

            <div style={styles.detailGrid}>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Town</div>
                <div style={styles.detailValue}>{walker.town || "—"}</div>
              </div>

              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>County</div>
                <div style={styles.detailValue}>{walker.county || "—"}</div>
              </div>

              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Home postcode</div>
                <div style={styles.detailValue}>{walker.homePostcode || "—"}</div>
              </div>

              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Service radius</div>
                <div style={styles.detailValue}>
                  {walker.serviceRadiusMiles} miles
                </div>
              </div>
            </div>
          </section>

          <aside style={styles.sideStack}>
            <section style={styles.sideCard}>
              <h2 style={styles.sectionTitleSmall}>Services</h2>

              {serviceTags.length > 0 ? (
                <div style={styles.tagList}>
                  {serviceTags.map((tag) => (
                    <span key={tag} style={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <p style={styles.bodyTextSmall}>No services listed yet.</p>
              )}
            </section>

            <section style={styles.sideCard}>
              <h2 style={styles.sectionTitleSmall}>Trust & checks</h2>

              <div style={styles.checkList}>
                <div style={styles.checkRow}>
                  <span style={styles.checkLabel}>DBS checked</span>
                  <span style={walker.dbsChecked ? styles.checkYes : styles.checkNo}>
                    {walker.dbsChecked ? "Yes" : "No"}
                  </span>
                </div>

                <div style={styles.checkRow}>
                  <span style={styles.checkLabel}>Insured</span>
                  <span style={walker.insured ? styles.checkYes : styles.checkNo}>
                    {walker.insured ? "Yes" : "No"}
                  </span>
                </div>

                <div style={styles.checkRow}>
                  <span style={styles.checkLabel}>Experience noted</span>
                  <span
                    style={
                      walker.experienceSummary?.trim()
                        ? styles.checkYes
                        : styles.checkNo
                    }
                  >
                    {walker.experienceSummary?.trim() ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </section>

            {sortedPhotos.length > 1 && (
              <section style={styles.sideCard}>
                <h2 style={styles.sectionTitleSmall}>Photos</h2>

                <div style={styles.thumbGrid}>
                  {sortedPhotos.map((photo) => (
                    <button
                      key={photo.photoId}
                      type="button"
                      onClick={() => setActivePhotoUrl(photo.url)}
                      style={styles.galleryThumbButton}
                    >
                      <img
                        src={photo.url}
                        alt={walker.displayTitle}
                        style={styles.thumb}
                      />
                    </button>
                  ))}
                </div>
              </section>
            )}
          </aside>
        </div>

        <section id="contact-section" style={styles.ctaCard}>
          <div style={styles.formIntro}>
            <h2 style={styles.ctaTitle}>Send an enquiry</h2>
            <p style={styles.ctaText}>
              Tell this walker a little about your dog, your area, and the support
              you need.
            </p>
          </div>

          {submitState === "submitted" && submitSuccess && (
            <div style={styles.successBox}>
              <div>{submitSuccess}</div>

              {ownerReplyPath && (
                <div style={styles.successActions}>
                  <Link to={ownerReplyPath} style={styles.successPrimaryLink}>
                    Open secure conversation now
                  </Link>
                  <Link to="/reply-access" style={styles.successSecondaryLink}>
                    Lost access later? Recover by email
                  </Link>
                </div>
              )}
            </div>
          )}

          {submitState === "error" && submitError && (
            <div style={styles.submitErrorBox}>{submitError}</div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.field}>
                <label htmlFor="ownerName" style={styles.label}>
                  Your name
                </label>
                <input
                  id="ownerName"
                  type="text"
                  value={form.ownerName}
                  onChange={(e) => updateForm("ownerName", e.target.value)}
                  style={styles.input}
                  placeholder="Your name"
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="ownerEmail" style={styles.label}>
                  Your email *
                </label>
                <input
                  id="ownerEmail"
                  type="email"
                  value={form.ownerEmail}
                  onChange={(e) => updateForm("ownerEmail", e.target.value)}
                  style={styles.input}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="ownerPostcode" style={styles.label}>
                  Your postcode
                </label>
                <input
                  id="ownerPostcode"
                  type="text"
                  value={form.ownerPostcode}
                  onChange={(e) => updateForm("ownerPostcode", e.target.value)}
                  style={styles.input}
                  placeholder="e.g. IP1 1AA"
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="walkFrequency" style={styles.label}>
                  Walk frequency
                </label>
                <input
                  id="walkFrequency"
                  type="text"
                  value={form.walkFrequency}
                  onChange={(e) => updateForm("walkFrequency", e.target.value)}
                  style={styles.input}
                  placeholder="e.g. 3 times a week"
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="dogName" style={styles.label}>
                  Dog name
                </label>
                <input
                  id="dogName"
                  type="text"
                  value={form.dogName}
                  onChange={(e) => updateForm("dogName", e.target.value)}
                  style={styles.input}
                  placeholder="Dog name"
                />
              </div>

              <div style={styles.field}>
                <label htmlFor="dogBreed" style={styles.label}>
                  Dog breed
                </label>
                <input
                  id="dogBreed"
                  type="text"
                  value={form.dogBreed}
                  onChange={(e) => updateForm("dogBreed", e.target.value)}
                  style={styles.input}
                  placeholder="Breed"
                />
              </div>

              <div style={styles.fieldFull}>
                <label htmlFor="dogAgeText" style={styles.label}>
                  Dog age
                </label>
                <input
                  id="dogAgeText"
                  type="text"
                  value={form.dogAgeText}
                  onChange={(e) => updateForm("dogAgeText", e.target.value)}
                  style={styles.input}
                  placeholder="e.g. 2 years old"
                />
              </div>

              <div style={styles.fieldFull}>
                <label htmlFor="message" style={styles.label}>
                  Message *
                </label>
                <textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => updateForm("message", e.target.value)}
                  style={styles.textarea}
                  placeholder="Tell the walker what kind of support you need."
                  required
                  rows={6}
                />
              </div>
            </div>

            <div style={styles.formActions}>
              <button
                type="submit"
                style={styles.submitButton}
                disabled={submitState === "submitting"}
              >
                {submitState === "submitting" ? "Sending enquiry..." : "Send enquiry"}
              </button>

              <Link
                to={`/walkers${location.search || ""}`}
                style={styles.secondaryLinkButton}
              >
                Keep browsing walkers
              </Link>
            </div>
          </form>
        </section>

        <div style={styles.mobileStickyBar}>
          <a href="#contact-section" style={styles.mobileStickyButton}>
            Enquire about this walker
          </a>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242, 138, 22, 0.16), transparent 28%), linear-gradient(180deg, #2f4f46 0%, #213a33 100%)",
    padding: "32px 16px 90px",
    color: "#ffffff",
  },
  container: {
    maxWidth: 1160,
    margin: "0 auto",
  },
  topRow: {
    marginBottom: 18,
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: "rgba(255,255,255,0.9)",
    textDecoration: "none",
    fontWeight: 700,
  },
  heroCard: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
    marginBottom: 22,
  },
  heroGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(280px, 360px)",
    gap: 24,
  },
  badgeRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.12)",
  },
  badgeSoft: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    background: "rgba(242, 138, 22, 0.18)",
    border: "1px solid rgba(242, 138, 22, 0.28)",
    color: "#fff7ef",
  },
  title: {
    margin: "0 0 8px",
    fontSize: 42,
    lineHeight: 1.02,
    letterSpacing: "-0.04em",
    fontWeight: 900,
  },
  nameLine: {
    margin: "0 0 8px",
    fontSize: 18,
    color: "rgba(255,255,255,0.92)",
    fontWeight: 700,
  },
  location: {
    margin: "0 0 10px",
    fontSize: 18,
    color: "rgba(255,255,255,0.86)",
    fontWeight: 700,
  },
  distanceLine: {
    margin: "0 0 14px",
    fontSize: 14,
    color: "rgba(255,255,255,0.74)",
  },
  intro: {
    margin: "0 0 22px",
    fontSize: 17,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.82)",
    maxWidth: 760,
  },
  rebookBanner: {
    borderRadius: 18,
    padding: 16,
    marginBottom: 18,
    background: "rgba(242, 138, 22, 0.16)",
    border: "1px solid rgba(242, 138, 22, 0.28)",
  },
  rebookBannerTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#ffffff",
    marginBottom: 6,
  },
  rebookBannerText: {
    fontSize: 14,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.92)",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
  },
  summaryCard: {
    background: "rgba(255,255,255,0.08)",
    borderRadius: 20,
    padding: 16,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  summaryLabel: {
    fontSize: 13,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "rgba(255,255,255,0.68)",
    marginBottom: 8,
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 800,
    color: "#ffffff",
  },
  sidePanel: {
    display: "flex",
    alignItems: "stretch",
  },
  enquiryCard: {
    width: "100%",
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 24,
    padding: 22,
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.12)",
  },
  photo: {
    width: "100%",
    height: 220,
    objectFit: "cover",
    borderRadius: 18,
    marginBottom: 12,
    background: "#ebe2d4",
    display: "block",
  },
  photoPlaceholder: {
    width: "100%",
    height: 220,
    display: "grid",
    placeItems: "center",
    borderRadius: 18,
    marginBottom: 12,
    background: "#ebe2d4",
    fontSize: 60,
  },
  photoThumbRow: {
    display: "flex",
    gap: 8,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  thumbButton: {
    width: 56,
    height: 56,
    borderRadius: 12,
    overflow: "hidden",
    padding: 0,
    border: "2px solid transparent",
    background: "#ebe2d4",
    cursor: "pointer",
  },
  thumbButtonActive: {
    border: "2px solid #f28a16",
  },
  thumbButtonImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  panelTitle: {
    margin: "0 0 10px",
    fontSize: 24,
    lineHeight: 1.1,
    color: "#213a33",
  },
  panelText: {
    margin: "0 0 14px",
    lineHeight: 1.65,
    color: "rgba(24, 32, 29, 0.78)",
  },
  trustListCompact: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 18,
  },
  trustPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: 999,
    background: "rgba(47, 79, 70, 0.08)",
    color: "#213a33",
    fontWeight: 700,
    fontSize: 13,
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(280px, 360px)",
    gap: 22,
    marginBottom: 22,
  },
  mainCard: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  sideStack: {
    display: "grid",
    gap: 18,
    alignContent: "start",
  },
  sideCard: {
    background: "rgba(255,255,255,0.08)",
    borderRadius: 24,
    padding: 20,
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.12)",
  },
  sectionTitle: {
    margin: "0 0 14px",
    fontSize: 28,
    lineHeight: 1.08,
    letterSpacing: "-0.03em",
    color: "#213a33",
  },
  sectionTitleSmall: {
    margin: "0 0 14px",
    fontSize: 22,
    lineHeight: 1.08,
    letterSpacing: "-0.03em",
    color: "#ffffff",
  },
  bodyText: {
    margin: "0 0 20px",
    lineHeight: 1.8,
    color: "rgba(24, 32, 29, 0.82)",
    fontSize: 16,
  },
  bodyTextSmall: {
    margin: 0,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.82)",
    fontSize: 15,
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
  },
  detailItem: {
    background: "rgba(24, 32, 29, 0.05)",
    borderRadius: 18,
    padding: 16,
  },
  detailLabel: {
    fontSize: 13,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "rgba(24, 32, 29, 0.56)",
    marginBottom: 8,
  },
  detailValue: {
    fontSize: 16,
    fontWeight: 700,
    color: "#18201d",
  },
  tagList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    padding: "9px 12px",
    borderRadius: 999,
    background: "rgba(242, 138, 22, 0.18)",
    border: "1px solid rgba(242, 138, 22, 0.28)",
    color: "#fff7ef",
    fontWeight: 800,
    fontSize: 14,
  },
  checkList: {
    display: "grid",
    gap: 12,
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 16,
    background: "rgba(255,255,255,0.06)",
  },
  checkLabel: {
    fontWeight: 700,
    color: "#ffffff",
  },
  checkYes: {
    fontWeight: 800,
    color: "#d4ffd9",
  },
  checkNo: {
    fontWeight: 800,
    color: "#ffd9d9",
  },
  thumbGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 10,
  },
  galleryThumbButton: {
    padding: 0,
    border: 0,
    background: "transparent",
    cursor: "pointer",
  },
  thumb: {
    width: "100%",
    height: 110,
    objectFit: "cover",
    borderRadius: 14,
    background: "#ebe2d4",
    display: "block",
  },
  ctaCard: {
    borderRadius: 28,
    padding: 26,
    background: "linear-gradient(180deg, #f28a16 0%, #da760b 100%)",
    color: "#ffffff",
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  formIntro: {
    marginBottom: 18,
  },
  ctaTitle: {
    margin: "0 0 10px",
    fontSize: 30,
    lineHeight: 1.05,
    letterSpacing: "-0.03em",
  },
  ctaText: {
    margin: 0,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.92)",
    maxWidth: 860,
  },
  successBox: {
    background: "rgba(212, 255, 217, 0.18)",
    color: "#ffffff",
    border: "1px solid rgba(212, 255, 217, 0.38)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
    fontWeight: 700,
    lineHeight: 1.7,
  },
  successActions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 14,
  },
  successPrimaryLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 800,
    background: "#213a33",
    color: "#ffffff",
    textDecoration: "none",
  },
  successSecondaryLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 800,
    background: "rgba(255,255,255,0.14)",
    color: "#ffffff",
    textDecoration: "none",
    border: "1px solid rgba(255,255,255,0.24)",
  },
  submitErrorBox: {
    background: "rgba(155, 28, 28, 0.18)",
    color: "#ffffff",
    border: "1px solid rgba(255, 219, 219, 0.34)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
    fontWeight: 700,
  },
  form: {
    display: "grid",
    gap: 18,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },
  field: {
    display: "grid",
    gap: 8,
  },
  fieldFull: {
    display: "grid",
    gap: 8,
    gridColumn: "1 / -1",
  },
  label: {
    fontWeight: 800,
    fontSize: 14,
    color: "#ffffff",
  },
  input: {
    width: "100%",
    font: "inherit",
    padding: "14px 14px",
    borderRadius: 14,
    outline: "none",
    border: "1px solid rgba(255,255,255,0.24)",
    background: "rgba(255,255,255,0.12)",
    color: "#ffffff",
  },
  textarea: {
    width: "100%",
    font: "inherit",
    padding: "14px 14px",
    borderRadius: 14,
    outline: "none",
    border: "1px solid rgba(255,255,255,0.24)",
    background: "rgba(255,255,255,0.12)",
    color: "#ffffff",
    resize: "vertical",
    minHeight: 140,
  },
  formActions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  submitButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    background: "#213a33",
    color: "#ffffff",
  },
  loadingCard: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#ffffff",
    borderRadius: 24,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
    fontSize: 18,
    fontWeight: 700,
  },
  errorCard: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 24,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  errorTitle: {
    margin: "0 0 10px",
    fontSize: 28,
    lineHeight: 1.08,
    color: "#213a33",
  },
  errorText: {
    margin: "0 0 18px",
    lineHeight: 1.7,
    color: "rgba(24, 32, 29, 0.8)",
  },
  errorActions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    background: "#f28a16",
    color: "#ffffff",
  },
  primaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    background: "#f28a16",
    color: "#ffffff",
    textDecoration: "none",
  },
  secondaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    background: "#f5f0e8",
    color: "#213a33",
    textDecoration: "none",
  },
  mobileStickyBar: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    padding: "12px 16px",
    background: "rgba(24, 42, 37, 0.92)",
    backdropFilter: "blur(8px)",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    zIndex: 60,
  },
  mobileStickyButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "14px 18px",
    borderRadius: 16,
    background: "#f28a16",
    color: "#ffffff",
    fontWeight: 800,
    textDecoration: "none",
  },
};

export default WalkerDetailPage;